import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { relativeTime } from './helpers';

// MESSAGE CORRECTIONS (AI Info "Global knowledge" sub-tab). Shows the corrections
// log — where a human accepted, edited, or discarded the AI's drafted reply —
// alongside whether a learned memory was minted from that correction, with an
// Ignore/Restore toggle for that memory. Fetches GET /api/aiinfo/corrections.
// Non-fatal load: an error shows the banner pattern, never blanks the surface.
// The Ignore toggle flips the memory's soft "active" flag via
// POST /aiinfo/memory/{memory_id} {action:'ignore'|'restore'}.

// A corrections-log row from /aiinfo/corrections.entries[]. Typed inline (per the
// collision-avoidance rule: no additions to types/index.ts).
interface CorrectionRow {
  draft_id: number;
  thread_id?: number | null;
  message_id?: number | null;
  draft_text?: string | null;
  final_text?: string | null;
  status?: string | null;
  edited?: boolean | null;
  inbound_text?: string | null;
  memory_id?: number | null;
  memory_active?: boolean | null;
  created_at?: string | null;
  resolved_by?: string | null;
}

const MessageCorrections: React.FC = () => {
  const [rows, setRows] = useState<CorrectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Soft per-endpoint error strings returned by the backend (errors[]).
  const [softErrors, setSoftErrors] = useState<string[]>([]);
  // memory_id currently toggling (drives per-row disabled + label).
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/aiinfo/corrections');
      setRows(Array.isArray(res.data?.entries) ? res.data.entries : []);
      setSoftErrors(Object.values(res.data?.errors || {}) as string[]);
    } catch (err) {
      setError('Failed to load message corrections');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Toggle the minted memory's soft active flag, then refresh.
  const toggleMemory = async (r: CorrectionRow) => {
    if (r.memory_id == null) return;
    const action = r.memory_active ? 'ignore' : 'restore';
    if (action === 'ignore' && !window.confirm('Ignore this learned memory? The AI will stop referencing it. You can restore it later.')) return;
    try {
      setBusyId(r.memory_id);
      setActionError(null);
      await api.post('/aiinfo/memory/' + r.memory_id, { action });
      await load();
    } catch (err) {
      setActionError('Failed to ' + action + ' memory #' + r.memory_id);
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const isDiscarded = (r: CorrectionRow): boolean =>
    r.status === 'discarded' || !r.final_text;

  return (
    <div className="flex-1 overflow-y-auto overflow-x-auto p-3 sm:p-6 bg-gray-50">
      <div className="w-full max-w-[1800px] mx-auto">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Message corrections <span className="font-normal text-gray-500">— what the AI drafted vs. what a human actually sent</span></h3>
          </div>
          <button onClick={() => load()} className="text-xs font-medium text-brand-700 hover:bg-brand-50 px-2 py-1 rounded">Refresh</button>
        </div>

        {/* Soft backend errors (partial fetch failure) — non-fatal banner. */}
        {softErrors.length > 0 && (
          <div className="mb-2 bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
            {softErrors.map((e, i) => (<div key={i}>{e}</div>))}
          </div>
        )}

        {/* Action error banner — isolated so a toggle failure never blanks the table. */}
        {actionError && (
          <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center justify-between">
            <span>{actionError}</span>
            <button onClick={() => setActionError(null)} className="text-red-500 hover:text-red-700 ml-3">✕</button>
          </div>
        )}

        {loading && rows.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-700"></div>
          </div>
        )}

        {error && rows.length === 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 ml-3">✕</button>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No message corrections yet
          </div>
        )}

        {rows.length > 0 && (
          <>
            {/* Non-blocking list-error banner once rows already exist. */}
            {error && (
              <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Inbound</th>
                    <th className="text-left font-medium px-3 py-2">AI generated</th>
                    <th className="text-left font-medium px-3 py-2">Actually sent / Discarded</th>
                    <th className="text-left font-medium px-3 py-2">Status</th>
                    <th className="text-left font-medium px-3 py-2">Thread</th>
                    <th className="text-left font-medium px-3 py-2">When</th>
                    <th className="text-right font-medium px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => {
                    const discarded = isDiscarded(r);
                    const hasMemory = r.memory_id != null;
                    const rowBusy = hasMemory && busyId === r.memory_id;
                    return (
                      <tr key={r.draft_id} className="hover:bg-gray-50 align-top">
                        <td className="px-3 py-2 text-gray-800 align-top min-w-[200px] max-w-[380px]">
                          <div className="whitespace-pre-wrap break-words">{r.inbound_text || '—'}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-600 align-top min-w-[220px] max-w-[440px]">
                          <div className="whitespace-pre-wrap break-words">{r.draft_text || '—'}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-700 align-top min-w-[220px] max-w-[440px]">
                          {discarded ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">Discarded</span>
                          ) : (
                            <div className="whitespace-pre-wrap break-words">
                              {r.final_text || '—'}
                              {r.edited ? <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 align-middle">edited</span> : null}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={'px-2 py-0.5 rounded-full text-xs font-medium ' +
                            (discarded ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700')}>
                            {r.status || (discarded ? 'discarded' : 'sent')}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                          {r.thread_id != null ? (
                            <a href={'#/threads/' + r.thread_id} title={'Thread #' + r.thread_id}
                              className="text-brand-700 hover:underline">#{r.thread_id}</a>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-500" title={r.created_at || undefined}>
                          {relativeTime(r.created_at)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">
                          {hasMemory ? (
                            r.memory_active ? (
                              <button onClick={() => toggleMemory(r)} disabled={rowBusy}
                                className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50">
                                {rowBusy ? '…' : 'Ignore memory'}
                              </button>
                            ) : (
                              <button onClick={() => toggleMemory(r)} disabled={rowBusy}
                                className="px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-50">
                                {rowBusy ? '…' : 'Restore'}
                              </button>
                            )
                          ) : (
                            <span className="text-gray-300" title="No learned memory was minted from this correction">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessageCorrections;
